/**
 * Facility-aware primary navigation.
 *
 * The staff nav differs by facility type so each vertical sees only what's
 * relevant to its daily loop:
 *   - clinic   → dashboard, check-in, appointments, reminders, patients, settings
 *   - lab      → dashboard (worklist), walk-in patients, settings
 *
 * Both the desktop header and the mobile drawer consume this single source
 * so the two can never drift apart. Routes referenced here must all exist in
 * the router.
 */
import type { FacilityType, Permission } from "@klyra/shared";
import {
	Activity,
	AlertTriangle,
	Beaker,
	BedDouble,
	BellRing,
	Boxes,
	CalendarDays,
	Camera,
	ClipboardCheck,
	FlaskConical,
	Home,
	Image as ImageIcon,
	Inbox,
	ListChecks,
	ListPlus,
	type LucideIcon,
	MapPinned,
	Pill,
	Receipt,
	RotateCcw,
	ScanLine,
	Send,
	Settings,
	Star,
	Timer,
	Truck,
	UserPlus,
	Users,
	Wallet,
} from "lucide-react";

export type NavTo =
	| "/dashboard"
	| "/check-in"
	| "/appointments"
	| "/reminders"
	| "/waitlist"
	| "/patients"
	| "/critical-alerts"
	| "/qc"
	| "/tat"
	| "/inventory"
	| "/walk-in"
	| "/revenue"
	| "/home-collections"
	| "/referrals-out"
	| "/radiology/worklist"
	| "/radiology/incoming"
	| "/radiology/critical-findings"
	| "/radiology/tat"
	| "/pos"
	| "/pharmacy/sales"
	| "/pharmacy/prescriptions"
	| "/pharmacy/procurement"
	| "/pharmacy/inventory"
	| "/supplies"
	| "/cohort-insights"
	| "/recalls"
	| "/photo-followups"
	| "/tasks"
	| "/clinical/inbox"
	| "/finance"
	| "/reviews"
	| "/inpatient"
	| "/settings";

export interface NavItem {
	to: NavTo;
	icon: LucideIcon;
	i18nKey: string;
	/**
	 * Marks a daily-loop essential. The mobile bottom bar shows exactly the
	 * `primary` items (≤4) plus a "More" button that opens the full drawer.
	 * Without this gate a 13-item clinic nav crammed every icon into the bar.
	 */
	primary?: boolean;
	/**
	 * Also rendered inline in the desktop header — but only from xl (1280px)
	 * up. A 13-item clinic nav doesn't fit a 1366×768 laptop once the search/
	 * create/bell controls take their share, so the header shows `primary`
	 * always, `desktop` from xl, and tucks the rest behind a "More" dropdown.
	 */
	desktop?: boolean;
	/**
	 * Opt-in live count bubble rendered by <NavItemBadge>. Data-driven so the
	 * header chips, the overflow menu and the mobile drawer stay in sync.
	 */
	badge?:
		| "rx_pending"
		| "tasks_open"
		| "results_inbox"
		| "radiology_incoming"
		| "critical_alerts";
	/**
	 * Restrict a clinic entry to specific business types (the clinic's
	 * specialty, e.g. "cosmetic"/"dental"). Omitted = shown to every clinic.
	 * Lets the generic clinic nav hide specialty-only surfaces (photo
	 * follow-ups belong to aesthetic/derma, not a GP). Filtered in navItemsFor
	 * when a businessType is supplied — mirrors SpecialtyCharts' gating.
	 */
	businessTypes?: ReadonlyArray<string>;
	/**
	 * Restrict the entry to specific clinic roles (e.g. the results inbox is
	 * a doctor/owner queue — a receptionist can't action it). Omitted = every
	 * role sees the item. Filtering happens in navItemsFor.
	 */
	roles?: ReadonlyArray<string>;
	/**
	 * Hide the entry unless the caller holds this permission. The finance/P&L
	 * surface is `billing.view`-gated server-side; showing it to a nurse/doctor
	 * without the permission just dead-ends in a 403. Filtered in navItemsFor
	 * when a hasPermission predicate is supplied.
	 */
	permission?: Permission;
}

const CLINIC_NAV: ReadonlyArray<NavItem> = [
	{ to: "/dashboard", icon: Home, i18nKey: "nav.dashboard", primary: true },
	// Tasks stays a daily surface (badge + desktop header) but yields its mobile
	// thumb-bar slot to check-in — the receptionist's most-used screen. It's one
	// tap away under "More".
	{
		to: "/tasks",
		icon: ListChecks,
		i18nKey: "nav.tasks",
		desktop: true,
		badge: "tasks_open",
	},
	// Doctor's unified results-review queue (Epic In-Basket style).
	{
		to: "/clinical/inbox",
		icon: Inbox,
		i18nKey: "nav.results_inbox",
		desktop: true,
		badge: "results_inbox",
		roles: ["doctor", "owner"],
	},
	// Check-in is the front-desk's highest-frequency action → mobile primary.
	{
		to: "/check-in",
		icon: ClipboardCheck,
		i18nKey: "nav.check_in",
		primary: true,
	},
	{
		to: "/appointments",
		icon: CalendarDays,
		i18nKey: "nav.appointments",
		primary: true,
	},
	{ to: "/reminders", icon: BellRing, i18nKey: "nav.reminders" },
	// Waitlist: patients waiting for an earlier slot (freed-slot offers).
	{ to: "/waitlist", icon: ListPlus, i18nKey: "nav.waitlist" },
	{ to: "/patients", icon: Users, i18nKey: "nav.patients", primary: true },
	{ to: "/referrals-out", icon: Send, i18nKey: "nav.referrals_out" },
	// Distinct icon from /reminders' bell so the two queues don't read alike.
	{ to: "/recalls", icon: RotateCcw, i18nKey: "nav.recalls" },
	{
		to: "/photo-followups",
		icon: Camera,
		i18nKey: "nav.photo_followups",
		businessTypes: ["cosmetic", "dermatology", "hair_transplant"],
	},
	{ to: "/supplies", icon: Boxes, i18nKey: "nav.supplies" },
	{ to: "/cohort-insights", icon: MapPinned, i18nKey: "nav.cohort" },
	{
		to: "/finance",
		icon: Wallet,
		i18nKey: "nav.finance",
		desktop: true,
		permission: "billing.view",
	},
	{ to: "/reviews", icon: Star, i18nKey: "nav.reviews" },
	{ to: "/settings", icon: Settings, i18nKey: "nav.settings", desktop: true },
];

const LAB_NAV: ReadonlyArray<NavItem> = [
	{ to: "/dashboard", icon: Home, i18nKey: "nav.dashboard", primary: true },
	// Tasks yields its thumb-bar slot to critical-alerts (a safety surface that
	// must be one tap away on mobile). Still badged + in the desktop header.
	{
		to: "/tasks",
		icon: ListChecks,
		i18nKey: "nav.tasks",
		desktop: true,
		badge: "tasks_open",
	},
	{ to: "/walk-in", icon: UserPlus, i18nKey: "nav.walk_in", primary: true },
	{ to: "/home-collections", icon: Home, i18nKey: "nav.home_collections" },
	{
		to: "/patients",
		icon: FlaskConical,
		i18nKey: "nav.lab_patients",
		primary: true,
	},
	{ to: "/qc", icon: Activity, i18nKey: "nav.qc" },
	{ to: "/tat", icon: Timer, i18nKey: "nav.tat" },
	{ to: "/inventory", icon: Beaker, i18nKey: "nav.inventory" },
	{ to: "/cohort-insights", icon: MapPinned, i18nKey: "nav.cohort" },
	// Patient-safety surface: mobile-primary + live count so an unactioned
	// critical value is never buried under "More".
	{
		to: "/critical-alerts",
		icon: AlertTriangle,
		i18nKey: "nav.critical_alerts",
		primary: true,
		desktop: true,
		badge: "critical_alerts",
	},
	// /revenue intentionally NOT in the nav: it duplicated the "collected"
	// slice of /finance under a second name. It stays routable as the
	// operations drill-down linked from the finance page.
	{
		to: "/finance",
		icon: Wallet,
		i18nKey: "nav.finance",
		desktop: true,
		permission: "billing.view",
	},
	{ to: "/settings", icon: Settings, i18nKey: "nav.settings", desktop: true },
];

const PHARMACY_NAV: ReadonlyArray<NavItem> = [
	{ to: "/dashboard", icon: Home, i18nKey: "nav.dashboard", primary: true },
	{
		to: "/tasks",
		icon: ListChecks,
		i18nKey: "nav.tasks",
		primary: true,
		badge: "tasks_open",
	},
	{ to: "/pos", icon: ScanLine, i18nKey: "nav.pos", primary: true },
	{ to: "/pharmacy/sales", icon: Receipt, i18nKey: "nav.sales", primary: true },
	{
		to: "/pharmacy/prescriptions",
		icon: Inbox,
		i18nKey: "nav.rx_incoming",
		desktop: true,
		badge: "rx_pending",
	},
	{
		to: "/pharmacy/procurement",
		icon: Truck,
		i18nKey: "nav.procurement",
		desktop: true,
	},
	// Direct stock surface — pricing/quantities were only reachable inside the
	// dashboard, so a POS hit on an unpriced item used to dead-end.
	{
		to: "/pharmacy/inventory",
		icon: Boxes,
		i18nKey: "nav.inventory",
		desktop: true,
	},
	{ to: "/patients", icon: Pill, i18nKey: "nav.lab_patients", desktop: true },
	{ to: "/settings", icon: Settings, i18nKey: "nav.settings", desktop: true },
];

const RADIOLOGY_NAV: ReadonlyArray<NavItem> = [
	{ to: "/dashboard", icon: Home, i18nKey: "nav.dashboard", primary: true },
	{
		to: "/tasks",
		icon: ListChecks,
		i18nKey: "nav.tasks",
		primary: true,
		badge: "tasks_open",
	},
	{
		to: "/radiology/worklist",
		icon: ImageIcon,
		i18nKey: "nav.radiology_worklist",
		primary: true,
	},
	{
		to: "/radiology/incoming",
		icon: Inbox,
		i18nKey: "nav.radiology_incoming",
		desktop: true,
		badge: "radiology_incoming",
	},
	{ to: "/patients", icon: Users, i18nKey: "nav.lab_patients", primary: true },
	{
		to: "/radiology/critical-findings",
		icon: AlertTriangle,
		i18nKey: "nav.critical_findings",
		desktop: true,
	},
	{ to: "/radiology/tat", icon: Timer, i18nKey: "nav.tat" },
	// /revenue dropped from the nav — see the lab nav note above.
	{
		to: "/finance",
		icon: Wallet,
		i18nKey: "nav.finance",
		permission: "billing.view",
	},
	{ to: "/settings", icon: Settings, i18nKey: "nav.settings", desktop: true },
];

/** Inpatient/HIS nav item — appended only when clinic.inpatient_enabled. */
export const INPATIENT_NAV_ITEM: NavItem = {
	to: "/inpatient",
	icon: BedDouble,
	i18nKey: "nav.inpatient",
};

/**
 * The primary nav items for a facility. Unknown/undefined → clinic (the
 * default). Role-gated items (NavItem.roles) only appear when the caller's
 * role matches — omitting `role` hides them (safe default: an unknown role
 * never sees a queue it can't action).
 */
export function navItemsFor(
	facilityType: FacilityType | undefined | null,
	role?: string | null,
	hasPermission?: (p: Permission) => boolean,
	businessType?: string | null,
): ReadonlyArray<NavItem> {
	const base =
		facilityType === "lab"
			? LAB_NAV
			: facilityType === "pharmacy"
				? PHARMACY_NAV
				: facilityType === "radiology"
					? RADIOLOGY_NAV
					: CLINIC_NAV;
	return base.filter((i) => {
		if (i.roles && !(role != null && i.roles.includes(role))) return false;
		// Permission-gated items hide unless the predicate is supplied AND grants
		// it. No predicate → hide the gated item (safe default: never show a
		// surface the user will 403 on).
		if (i.permission && !hasPermission?.(i.permission)) return false;
		// Specialty-gated items hide unless the clinic's business type matches.
		// No businessType known → keep the item (safe default: don't hide a
		// surface just because the specialty wasn't resolved yet).
		if (
			i.businessTypes &&
			businessType != null &&
			!i.businessTypes.includes(businessType)
		)
			return false;
		return true;
	});
}

/** Facility nav + the inpatient item when the capability is enabled (before settings). */
export function navItemsWithCapabilities(
	facilityType: FacilityType | undefined | null,
	opts: {
		inpatient?: boolean;
		role?: string | null;
		hasPermission?: (p: Permission) => boolean;
		businessType?: string | null;
	},
): ReadonlyArray<NavItem> {
	const base = navItemsFor(
		facilityType,
		opts.role,
		opts.hasPermission,
		opts.businessType,
	);
	if (!opts.inpatient) return base;
	const idx = base.findIndex((i) => i.to === "/settings");
	if (idx === -1) return [...base, INPATIENT_NAV_ITEM];
	return [...base.slice(0, idx), INPATIENT_NAV_ITEM, ...base.slice(idx)];
}

/**
 * The ≤4 thumb-bar items for the mobile bottom bar. Falls back to the first
 * four nav items if a facility forgot to flag any `primary` (defensive — the
 * bar must never be empty).
 */
export function primaryNavItems(
	facilityType: FacilityType | undefined | null,
	role?: string | null,
	hasPermission?: (p: Permission) => boolean,
	businessType?: string | null,
): ReadonlyArray<NavItem> {
	const base = navItemsFor(facilityType, role, hasPermission, businessType);
	const flagged = base.filter((i) => i.primary).slice(0, 4);
	return flagged.length > 0 ? flagged : base.slice(0, 4);
}
